import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Mail, Send, Users, Sparkles, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const AdminEmailComposer = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedClientId = searchParams.get('client');
  
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const templates = [
    {
      name: 'Welcome Email',
      subject: 'Welcome to VowFlow! Your Journey Begins',
      body: `We\'re thrilled to have you join VowFlow!\n\nYour wedding planning journey starts here. We\'ve built beautiful tools to help you organize every detail with ease and joy.\n\nTake a moment to explore your dashboard, chat with Grace our AI assistant, and start building your perfect day.\n\nWe\'re here to support you every step of the way.`
    },
    {
      name: '30-Day Countdown',
      subject: 'One Month to Go! Your Wedding Countdown',
      body: `Can you believe it? Your wedding is just 30 days away!\n\nThis is such an exciting milestone. Here\'s what to focus on this month:\n- Finalize your guest count\n- Confirm all vendor details\n- Send final information to your wedding party\n- Create your seating chart\n\nRemember to take moments to breathe and enjoy this special time. You\'ve got this!`
    },
    {
      name: 'Check-In Email',
      subject: 'How\'s Wedding Planning Going?',
      body: `We wanted to check in and see how your wedding planning is progressing!\n\nVowFlow is here to make your journey smooth and enjoyable. If you have any questions or need help with any features, please don't hesitate to reach out.\n\nRemember, planning should be fun too! Take time to enjoy the process.`
    },
    {
      name: '7-Day Final Reminder',
      subject: 'Your Special Week is Here! Final Checklist',
      body: `Your wedding week has arrived! How exciting!\n\nFinal week checklist:\n- Touch base with all vendors\n- Confirm timeline with wedding party\n- Pack for honeymoon\n- Prepare vendor tips\n- Most importantly: rest and enjoy!\n\nYou\'ve planned beautifully. Now it\'s time to celebrate!`
    }
  ];

  useEffect(() => {
    loadClients();
  }, []);

  // Filter clients when search query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredClients(clients);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredClients(clients.filter(c => 
        c.partner1_name?.toLowerCase().includes(query) ||
        c.partner2_name?.toLowerCase().includes(query) ||
        c.email?.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, clients]);

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        headers: { 'admin-id': user.id }
      });
      const loadedClients = response.data.clients || [];
      setClients(loadedClients);
      setFilteredClients(loadedClients);
      
      // Pre-select client if passed via URL
      if (preSelectedClientId) {
        const clientExists = loadedClients.some(c => c.id === preSelectedClientId);
        if (clientExists) {
          setSelectedClients([preSelectedClientId]);
        }
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const handleSelectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };

  const handleSelectClient = (clientId) => {
    if (selectedClients.includes(clientId)) {
      setSelectedClients(selectedClients.filter(id => id !== clientId));
    } else {
      setSelectedClients([...selectedClients, clientId]);
    }
  };

  const applyTemplate = (template) => {
    setSubject(template.subject);
    setBody(template.body);
    toast.success('Template applied');
  };

  const handleSend = async () => {
    if (selectedClients.length === 0) {
      toast.error('Please select at least one client');
      return;
    }
    if (!subject || !body) {
      toast.error('Please enter subject and message');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/admin/send-client-email`,
        {
          client_ids: selectedClients,
          subject,
          body
        },
        { headers: { 'admin-id': user.id } }
      );

      toast.success(`Email sent to ${response.data.sent_count} client(s)! 📧`);
      setSubject('');
      setBody('');
      setSelectedClients([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f0f]">
      <nav className="bg-[#1C1917] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-white/5 rounded-full transition-colors">
              <ArrowLeft size={20} className="text-white/60" />
            </button>
            <Heart className="text-[#881337]" size={28} fill="#881337" />
            <span className="text-xl font-serif font-bold text-white">VowFlow</span>
            <span className="text-xs bg-[#881337] text-white px-2 py-0.5 rounded-full ml-2">ADMIN</span>
          </div>
        </div>
      </nav>

      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-serif text-white mb-2">Email Clients</h1>
          <p className="text-white/50">Send updates, reminders, and check-ins to your clients</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Selection */}
          <div className="lg:col-span-1">
            <div className="bg-[#1C1917] rounded-2xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-white">Select Clients</h2>
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-[#881337] hover:text-[#9F1239]"
                >
                  {selectedClients.length === filteredClients.length && filteredClients.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Search Bar */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/30" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#881337]"
                />
              </div>

              <p className="text-white/40 text-xs mb-3">
                {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''} 
                {searchQuery && ` matching "${searchQuery}"`}
                {selectedClients.length > 0 && ` • ${selectedClients.length} selected`}
              </p>

              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredClients.map((client) => (
                  <label
                    key={client.id}
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.id)}
                      onChange={() => handleSelectClient(client.id)}
                      className="w-4 h-4 text-[#881337] border-white/20 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{client.partner1_name} & {client.partner2_name}</p>
                      <p className="text-white/40 text-xs truncate">{client.email}</p>
                    </div>
                  </label>
                ))}
              </div>

              {selectedClients.length > 0 && (
                <div className="mt-4 p-3 bg-[#881337]/20 rounded-lg">
                  <p className="text-[#881337] text-sm">
                    {selectedClients.length} client(s) selected
                  </p>
                </div>
              )}
            </div>

            {/* Templates */}
            <div className="bg-[#1C1917] rounded-2xl p-6 border border-white/10 mt-6">
              <h2 className="text-lg font-medium text-white mb-4">Email Templates</h2>
              <div className="space-y-2">
                {templates.map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => applyTemplate(template)}
                    className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <p className="text-white text-sm">{template.name}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Email Composer */}
          <div className="lg:col-span-2">
            <div className="bg-[#1C1917] rounded-2xl p-6 border border-white/10">
              <h2 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
                <Mail size={20} />
                Compose Email
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Subject</label>
                  <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject..."
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                  />
                </div>

                <div>
                  <label className="text-white/60 text-sm mb-2 block">Message</label>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Write your message to clients...\n\nTip: Use line breaks to separate paragraphs for better formatting."
                    rows={12}
                    className="w-full bg-white/5 border border-white/10 rounded-lg p-4 text-white placeholder:text-white/30 focus:ring-2 focus:ring-[#881337] focus:border-[#881337]"
                  />
                </div>

                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <p className="text-amber-300 text-sm">
                    <strong>Tip:</strong> Emails will be sent with VowFlow's elegant branding and formatting. Each client will receive a personalized email addressed to their couple names.
                  </p>
                </div>

                <Button
                  onClick={handleSend}
                  disabled={sending || selectedClients.length === 0 || !subject || !body}
                  className="w-full h-12 bg-[#881337] hover:bg-[#9F1239] text-white flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send to {selectedClients.length} Client(s)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEmailComposer;